import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import AdminService from "../../../api/services/adminService";
import "../Home/Homepage.css";
import "./BrokerageManagementPage.css";

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const userDisplayName = (user) => {
  const parts = [user?.first_name, user?.last_name].filter(Boolean);
  return parts.length ? parts.join(" ") : "—";
};

const fromUserDisplay = (u) => u?.name || u?.email || u?.phone || u?.uuid || "—";

const LEVELS = ["1", "2", "3", "4", "5"];

const BrokerageEarnerDetailsPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [user, setUser] = useState(null);
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, total_pages: 1 });

  const [levelFilter, setLevelFilter] = useState("");
  const [currencyFilter, setCurrencyFilter] = useState("");

  const fetchDetails = async (page = 1, overrides = {}) => {
    if (!userId) return;
    const level = overrides.level !== undefined ? overrides.level : levelFilter;
    const currency_id = overrides.currency_id !== undefined ? overrides.currency_id : currencyFilter;
    setLoading(true);
    setError("");
    try {
      const res = await AdminService.getBrokerageEarnerDetails(userId, {
        page,
        limit: pagination.limit,
        ...(level && { level }),
        ...(currency_id && { currency_id }),
      });
      if (res?.success) {
        setUser(res?.user || null);
        setSummary(res?.summary || null);
        setRows(Array.isArray(res?.data) ? res.data : []);
        setPagination((prev) => ({
          ...prev,
          ...(res?.pagination || {}),
          page: res?.pagination?.page || page,
        }));
      } else {
        setError(res?.message || "Failed to fetch brokerage details.");
      }
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Failed to fetch brokerage details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetails(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const handleApplyFilters = (e) => {
    e.preventDefault();
    fetchDetails(1);
  };

  const handleResetFilters = () => {
    setLevelFilter("");
    setCurrencyFilter("");
    fetchDetails(1, { level: "", currency_id: "" });
  };

  const byCurrency = Array.isArray(summary?.by_currency) ? summary.by_currency : [];

  return (
    <main className="dashboard-main dashboard-subpage brokerage-page">
      <section className="dashboard-hero brokerage-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <Link to="/dashboard/brokerage">Brokerage</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Earner Details</span>
          </p>
          <h1 className="dashboard-hero__title">Brokerage Earner Details</h1>
          <p className="dashboard-hero__subtitle">{userDisplayName(user)} {user?.email ? `· ${user.email}` : ""}</p>
        </div>
        <div className="brokerage-hero__icon">
          <i className="fas fa-user-circle" />
        </div>
      </section>

      <div className="dashboard-body">
        <button type="button" className="brokerage-back-btn" onClick={() => navigate("/dashboard/brokerage")} style={{ marginBottom: "1rem" }}>
          <i className="fas fa-arrow-left" /> Back to earners
        </button>

        {loading ? (
          <section className="brokerage-section">
            <div className="brokerage-empty"><i className="fas fa-spinner fa-spin" /><p>Loading details...</p></div>
          </section>
        ) : error ? (
          <section className="brokerage-section">
            <div className="brokerage-empty"><i className="fas fa-exclamation-circle" /><p>{error}</p></div>
          </section>
        ) : (
          <>
            <section className="brokerage-section">
              <h2 className="brokerage-section__title"><i className="fas fa-id-badge" /> User</h2>
              <div className="brokerage-summary-grid">
                <div><strong>Name:</strong> {userDisplayName(user)}</div>
                <div><strong>Email:</strong> {user?.email || "—"}</div>
                <div><strong>UUID:</strong> {user?.uuid || "—"}</div>
                <div><strong>Total Earned:</strong> {summary?.total_earned ?? 0}</div>
                <div><strong>Total Transactions:</strong> {summary?.total_transactions ?? 0}</div>
              </div>
            </section>

            <section className="brokerage-section">
              <h2 className="brokerage-section__title"><i className="fas fa-coins" /> By Currency</h2>
              {byCurrency.length === 0 ? (
                <div className="brokerage-empty"><p>No currency breakdown.</p></div>
              ) : (
                <div className="brokerage-table-wrap">
                  <table className="brokerage-table">
                    <thead>
                      <tr>
                        <th>Currency</th>
                        <th>Amount</th>
                        <th>Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {byCurrency.map((c) => (
                        <tr key={c.currency_id || c.currency}>
                          <td>{c.currency || "—"}</td>
                          <td>{c.amount ?? 0}</td>
                          <td>{c.count ?? 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <section className="brokerage-section">
              <h2 className="brokerage-section__title"><i className="fas fa-layer-group" /> By Level</h2>
              <div className="brokerage-level-grid">
                {LEVELS.map((lvl) => (
                  <div key={lvl} className="brokerage-level-card">
                    <span className="brokerage-level-card__lvl">Level {lvl}</span>
                    <span className="brokerage-level-card__amount">{summary?.by_level?.[lvl] ?? 0}</span>
                    <span className="brokerage-level-card__count">{summary?.by_level_count?.[lvl] ?? 0} txns</span>
                  </div>
                ))}
              </div>
            </section>

            <section className="brokerage-section">
              <h2 className="brokerage-section__title"><i className="fas fa-file-invoice-dollar" /> Brokerage Transactions</h2>

              <form className="brokerage-toolbar" onSubmit={handleApplyFilters}>
                <select value={levelFilter} onChange={(e) => setLevelFilter(e.target.value)} aria-label="Filter by level">
                  <option value="">All levels</option>
                  {LEVELS.map((lvl) => (
                    <option key={lvl} value={lvl}>Level {lvl}</option>
                  ))}
                </select>
                <select value={currencyFilter} onChange={(e) => setCurrencyFilter(e.target.value)} aria-label="Filter by currency">
                  <option value="">All currencies</option>
                  {byCurrency.map((c) => (
                    <option key={c.currency_id || c.currency} value={c.currency_id}>{c.currency}</option>
                  ))}
                </select>
                <button type="submit">Apply</button>
                <button type="button" className="brokerage-back-btn" onClick={handleResetFilters}>Reset</button>
              </form>

              {rows.length === 0 ? (
                <div className="brokerage-empty"><i className="fas fa-file-invoice-dollar" /><p>No transactions found</p></div>
              ) : (
                <>
                  <div className="brokerage-table-wrap">
                    <table className="brokerage-table">
                      <thead>
                        <tr>
                          <th>Level</th>
                          <th>From User</th>
                          <th>Trade ID</th>
                          <th>Currency</th>
                          <th>Trade Fee</th>
                          <th>Pool Amount</th>
                          <th>Pool %</th>
                          <th>Commission %</th>
                          <th>Commission Amount</th>
                          <th>Created At</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row) => (
                          <tr key={row._id}>
                            <td><span className="brokerage-level-pill">{row.level ?? "—"}</span></td>
                            <td>{fromUserDisplay(row.from_user)}</td>
                            <td>{row.trade_id || "—"}</td>
                            <td>{row.currency || "—"}</td>
                            <td>{row.trade_fee ?? "—"}</td>
                            <td>{row.brokerage_pool_amount ?? "—"}</td>
                            <td>{row.pool_percentage ?? "—"}</td>
                            <td>{row.commission_percentage ?? "—"}</td>
                            <td>{row.commission_amount ?? "—"}</td>
                            <td>{formatDate(row.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="brokerage-pagination">
                    <button type="button" onClick={() => fetchDetails(pagination.page - 1)} disabled={pagination.page <= 1}>Prev</button>
                    <span>Page {pagination.page} of {pagination.total_pages || 1}</span>
                    <button type="button" onClick={() => fetchDetails(pagination.page + 1)} disabled={pagination.page >= (pagination.total_pages || 1)}>Next</button>
                  </div>
                </>
              )}
            </section>
          </>
        )}
      </div>
    </main>
  );
};

export default BrokerageEarnerDetailsPage;
